import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, LOCALE_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  ProjectSteps,
  Projects,
  Staff,
  Personen,
  WebserviceResponse
} from '../../../_shared/interfaces';
import {
  ProjectsStoreService,
  PersonsStoreService,
  AlertService,
  IAlertType,
  StaffStoreService,
  CrmStoreService,
  ConfirmDialogService,
} from '../../../_shared/services';
import {
  ClrDatagridModule,
  ClrDatepickerModule,
  ClrInputModule, ClrNumberInputModule,
  ClrRadioModule,
  ClrSelectModule,
  ClrStepperModule
} from "@clr/angular";
import { DatePipe, JsonPipe, NgForOf, NgIf } from '@angular/common';
import { ClarityIcons, eyeIcon, minusIcon, plusIcon, timesCircleIcon, twoWayArrowsIcon } from "@cds/core/icon";

import { environment as env } from '../../../../environments/environment';
import { Subject } from "rxjs";
import { take } from "rxjs/operators";

import { sichtbareMitarbeiter } from "../../../_shared/utils/staff-selection";

@Component({
  selector: 'app-project-form',
  standalone: true,
  templateUrl: './project-form.component.html',
  imports: [
    ReactiveFormsModule,
    ClrStepperModule,
    ClrDatagridModule,
    ClrInputModule,
    ClrSelectModule,
    ClrDatepickerModule,
    DatePipe,
    RouterLink,
    NgIf,
    ClrRadioModule,
    NgForOf,
    JsonPipe,
    ClrNumberInputModule
  ],
  styleUrls: ['./project-form.component.sass'],
  providers: [{ provide: LOCALE_ID, useValue: 'de' }]
})

export class ProjectFormComponent implements OnInit, OnChanges {
  projectForm!: FormGroup;
  projectId: string = '';
  currentProject: string = '';
  internalCrmId: number = 0;
  private defaultDate: Date = new Date();
  /** Ungefilterte Antwort des Servers. Fuer die Anzeige siehe `staff`. */
  staffAll: Staff[] = [];

  /**
   * Nur aktive Mitarbeiter -- und zusaetzlich der aktuell zugewiesene, auch
   * wenn er passiv ist. Siehe `sichtbareMitarbeiter`; ohne diese Ausnahme
   * verloere das Bearbeiten eines Altvorgangs seinen Bearbeiter.
   */
  get staff(): Staff[] {
    return sichtbareMitarbeiter(this.staffAll, this.projectForm);
  }
  persons: Personen[] = [];
  newProject: any = {};

  @Input() isUpdateMode = false;
  @Input() projectsData?: Projects;
  @Input() companyId: number = 0;
  @Input() employeeLeader: number | null = null;
  @Input() employeePhm: number = 0;
  @Output() formSubmit = new EventEmitter<ProjectSteps>();

  devMode: boolean = false;
  crmMode: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private projss: ProjectsStoreService,
    private crmss: CrmStoreService,
    private alertDispatcher: AlertService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private router: Router,
    private staffService: StaffStoreService,
    private personsService: PersonsStoreService,
    private confirmDialog: ConfirmDialogService
  ) {
    ClarityIcons.addIcons(twoWayArrowsIcon, eyeIcon, timesCircleIcon, plusIcon, minusIcon);
    this.devMode = env.stages.dev;
    this.crmMode = env.externalCrm;
    this.initForm();
  }

  ngOnInit() {
    this.syncUpdateInputs();
    this.loadStaff();
    this.loadPersons(this.companyId);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectsData'] || changes['isUpdateMode'] || changes['companyId']) {
      this.syncUpdateInputs();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm() {
    this.projectForm = this.fb.group({
      stepI: this.fb.group({
        cid: this.companyId,
        name_project: ['', [Validators.required, Validators.maxLength(100)]],
        description: [''],
        request_id: [''],
        crm_id: [''],
        cosmos_id: 0,
        order_id: [''],
        status: ['running', [Validators.required]],
        budget: [0],
        start_project: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
        end_project: this.datePipe.transform(this.defaultDate, 'dd.MM.yyyy'),
      }),
      stepII: this.fb.group({
        employee_phm: [this.employeePhm],
      }),
      stepIII: this.fb.group({
        employee_leader: [this.employeeLeader],
      })
    });
  }

  private patchFormValues(data: Projects) {
    if (!data) return;

    const formValue = {
      stepI: {
        cid: data.cid || this.companyId,
        name_project: data.name_project || '',
        description: data.description || '',
        request_id: data.request_id || '',
        crm_id: data.crm_id ?? '',
        cosmos_id: data.cosmos_id || 0,
        order_id: data.order_id || '',
        status: data.status || 'running',
        budget: data.budget || 0,
        start_project: this.formatProjectDateForForm(data.start_project),
        end_project: this.formatProjectDateForForm(data.end_project)
      },
      stepII: {
        employee_phm: data.employee_phm || this.employeePhm,
      },
      stepIII: {
        employee_leader: data.employee_leader || this.employeeLeader,
      }
    };
    this.newProject = formValue;
    this.projectForm.reset(formValue);
    this.projectForm.updateValueAndValidity();
  }

  private formatProjectDateForForm(value: string | null | undefined): string {
    const raw = value?.trim() ?? '';
    if (!raw) {
      return '';
    }

    if (/^\d{2}\.\d{2}\.\d{4}$/.test(raw)) {
      return raw;
    }

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
      return raw;
    }

    return this.datePipe.transform(parsed, 'dd.MM.yyyy') ?? raw;
  }

  private syncUpdateInputs() {
    if (!this.isUpdateMode || !this.projectsData) {
      return;
    }

    this.currentProject = this.companyId + '.' + this.projectsData.id_project;
    if (this.crmMode) this.internalCrmId = this.projectsData.cosmos_id;
    this.projectId = this.projectsData.id_project;
    this.patchFormValues(this.projectsData);
  }

  projectLeader(): number {
    return this.projectForm.get('stepII')?.get('employee_phm')?.value || 0;
  }

  createProject() {
    this.runWithCrmGuard(() => this.createProjectInternal());
  }

  private createProjectInternal(onReady?: () => void) {
    if (this.projectForm.valid) {
      if (this.projectId) {
        onReady?.();
        return;
      }

      const stepI = this.projectForm.value.stepI;

      const parseGermanDate = (dateInput: string | Date): Date | null => {
        if (!dateInput) return null;

        if (typeof dateInput === 'string' && dateInput.includes('.')) {
          const [day, month, year] = dateInput.split('.');
          return new Date(+year, +month - 1, +day);
        }

        return new Date(dateInput);
      };

      const startDate = parseGermanDate(stepI.start_project);
      const endDate = parseGermanDate(stepI.end_project);

      const project: Projects = {
        budget: 0,
        cid: this.companyId,
        cid_project_id:  "",
        comment:  "",
        comment_phm:  "",
        company_name: "",
        cosmos_id:  this.internalCrmId ?? 0,
        description: stepI.description,
        employee_leader: this.employeeLeader,
        employee_phm: this.employeePhm,
        end_project: this.datePipe.transform(endDate, 'yyyy-MM-dd') ?? '',
        id_project: "",
        name_project: stepI.name_project,
        order_id:  '',
        request_id:  '',
        start_project: this.datePipe.transform(startDate, 'yyyy-MM-dd') ?? '',
        status:  "",
      }
      this.projss.startProject(this.companyId).subscribe({
        next: (response) => {
          this.projectId = response.message;
          if (this.devMode) console.log('249 - ' + this.projectId + ' message = ' + response.message)
          if (this.crmMode && this.crmss.getCrmToken()) {
            this.crmss.crmAddProject(project, this.projectId, this.employeePhm)
              .subscribe({
                next: tecartId => {
                  this.alertDispatcher.alertConfig.message += '...schreibe ins TecArt. ';
                  if (this.devMode) console.log('255 - ' + this.projectId + ' message = ' + response.message)
                  stepI.cosmos_id = tecartId;
                  this.internalCrmId = tecartId;
                },
                complete: () => {
                  onReady?.();
                  if (this.devMode) console.log('261 - ' + this.internalCrmId)
                },
                error: (error) => {
                  this.alertDispatcher.show({
                    message: 'Fehler beim Anlegen des TecArt-Projekts: ' + error,
                    clrAlertType: IAlertType.DANGER
                  }, 5000);
                }
              });
          } else {
            onReady?.();
          }
        },
        complete: () => {
          if (this.devMode) console.log('267 - ' + this.projectId)
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Erstellen des Projekts: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
          return
        }
      });
    }
  }

  updateProject() {
    this.runWithCrmGuard(() => {
      if (this.isUpdateMode) {
        this.updateProjectInternal('Projekt erfolgreich aktualisiert');
        return;
      }

      this.createProjectInternal(() => this.updateProjectInternal('Projekt erfolgreich gespeichert'));
    });
  }

  private updateProjectInternal(successMessage: string) {
    if (this.projectForm.valid) {
      const formValue = this.projectForm.value;
      this.projss.updateProject(formValue, this.companyId, this.projectId).subscribe({
        next: () => {
          this.alertDispatcher.show({
            message: successMessage,
            clrAlertType: IAlertType.SUCCESS
          }, 5000);
          this.formSubmit.emit(formValue);
          this.router.navigate(['/project/details', this.companyId, this.projectId]);
        },
        error: (error) => {
          this.alertDispatcher.show({
            message: 'Fehler beim Aktualisieren des Projekts: ' + error,
            clrAlertType: IAlertType.DANGER
          }, 5000);
        }
      });
    }
  }

  private runWithCrmGuard(action: () => void) {
    if (!this.crmMode) {
      action();
      return;
    }

    this.crmss.crmHealthCheck().pipe(take(1)).subscribe({
      next: (ok: boolean) => {
        if (!ok) {
          this.alertDispatcher.show({
            message: 'CRM ist nicht erreichbar. Speichern ist bei aktivem CRM gesperrt.',
            clrAlertType: IAlertType.DANGER
          }, 5000);
          return;
        }
        action();
      },
      error: () => {
        this.alertDispatcher.show({
          message: 'CRM-Status konnte nicht geprüft werden. Speichern wurde abgebrochen.',
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  deleteProject() {
    this.projss.getDeleteProjectImpact(this.companyId, this.projectId).subscribe({
      next: impactResponse => {
        const impact = impactResponse?.object?.[0];
        const intro = `Initialisierung von Projekt ${this.companyId}.${this.projectId} abbrechen?`;
        if (!this.confirmDialog.confirmProjectDelete(this.companyId, this.projectId, impact, intro)) {
          return;
        }

        this.projss.deleteProject(this.companyId, this.projectId)
          .subscribe({
            next: data => {
              if (!data['hasErrors']) {
                if (this.internalCrmId) {
                  this.crmss.crmDeleteProject(this.companyId, this.internalCrmId)
                }
              } else {
                return;
              }
            },
            complete: () => {
              this.alertDispatcher.alertConfig.message += 'Das Projekt ' + this.companyId + '.' + this.projectId + ' wurde gelöscht!';
              this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
              this.router.navigate(['/projects/overview'])
            }
          });
      },
      error: () => {
        const intro = `Initialisierung von Projekt ${this.companyId}.${this.projectId} abbrechen?`;
        if (this.confirmDialog.confirmProjectDelete(this.companyId, this.projectId, undefined, intro)) {
          this.projss.deleteProject(this.companyId, this.projectId)
            .subscribe({
              next: data => {
                if (!data['hasErrors']) {
                  if (this.internalCrmId) {
                    this.crmss.crmDeleteProject(this.companyId, this.internalCrmId)
                  }
                } else {
                  return;
                }
              },
              complete: () => {
                this.alertDispatcher.alertConfig.message += 'Das Projekt ' + this.companyId + '.' + this.projectId + ' wurde gelöscht!';
                this.alertDispatcher.alertConfig.clrAlertType = IAlertType.INFO;
                this.router.navigate(['/projects/overview'])
              }
            });
        }
      }
    });
  }

  private loadStaff() {
    this.staffService.getStaff().subscribe({
      next: (response: WebserviceResponse<Staff[]>) => {
        if (response?.object) {
          this.staffAll = response.object;
        }
      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Mitarbeiter: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
      }
    });
  }

  private loadPersons(companyId: number){
    this.personsService.getPersonInCompany(companyId, true).subscribe({
      next: (response: WebserviceResponse<Personen[]>) => {
        if (response?.object) {
          this.persons = response.object;
        }
      },
      complete: () => {
        // Sortiere die Liste nach dem Nachnamen
        this.persons.sort((a, b) => {
          if (a.lastname < b.lastname) {
            return -1;
          }
          if (a.lastname > b.lastname) {
            return 1;
          }
          return 0;
        });

      },
      error: (error: any) => {
        this.alertDispatcher.show({
          message: 'Fehler beim Laden der Kunden: ' + error,
          clrAlertType: IAlertType.DANGER
        }, 5000);
        return
      }
    });
  }
}
